
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import {
  ChatFeedbackInterface,
  FeedbackRepositoryInterface,
  FeedbackStatus,
} from '@/services/QC-V2/feedback/interfaces';
import {
  ChatRepositoryInterface,
  ChatReviewFormDataInterface,
  ChatReviewInterface,
  CommentCreateInterface,
  ProfileMapInterface,
} from '@/services/QC-V2/chat/interfaces';
import { Roles } from '@/services/Roles';
import { ScoreTypeInterface } from '@/services/QC-V2/score-type/interfaces';
import { ScoreGradeInterface } from '@/services/QC-V2/score-grade/interfaces';
import { CreateTagInterface, TagInterface, TagRepositoryInterface } from '@/services/QC-V2/tag/interfaces';
import { CommentRate, CommentRepositoryInterface } from '@/services/QC-V2/comment/interfaces';
import DeleteCommentDialog from '@/components/dialogs/qc-v2/comment/DeleteCommentDialog.vue';

@Component({
  mixins: [
    Themeable,
  ],
  components: {
    DeleteCommentDialog,
  },
  inject: [
    'feedbackRepositoryV2',
    'chatRepositoryV2',
    'tagRepositoryV2',
    'commentRepositoryV2',
  ],
})
export default class ChatFeedbackReview extends Vue {
  @Prop() private feedback!: ChatFeedbackInterface;
  @Prop() private profilesMap!: ProfileMapInterface;

  @Prop() private scoreTypesMap!: { [key: number]: string };
  @Prop() private scoreGradesMap!: { [key: number]: string };

  @Prop({ default: false }) private initiallyCollapsed!: boolean;

  @Prop({ default: () => [] }) private scoreTypes!: ScoreTypeInterface[];
  @Prop({ default: () => [] }) private scoreGrades!: ScoreGradeInterface[];
  @Prop({ default: () => [] }) private tags!: TagInterface[];

  private tagsMap: { [key: number]: TagInterface } = {};

  private readonly feedbackRepositoryV2!: FeedbackRepositoryInterface;
  private readonly chatRepositoryV2!: ChatRepositoryInterface;
  private readonly commentRepositoryV2!: CommentRepositoryInterface;
  private tagRepositoryV2!: TagRepositoryInterface;
  private loading: boolean = true;
  private formValid: boolean = false;
  private userRoles: object = Roles;

  private collapsed: boolean = false;
  private showReviewEditSection: boolean = false;

  private searchTag: string = '';

  private chatComment: string = '';
  private commentRates: typeof CommentRate = CommentRate;
  private commentRate: CommentRate = CommentRate.NOT_RATED;
  private editCommentRate: CommentRate = CommentRate.NOT_RATED;
  private commentIndexForEdit: number = -1;

  private commentIdForEdit: number = 0;
  private editCommentText: string = '';

  private deleteCommentDialogVisible: boolean = false;
  private deleteCommentDialogEvent!: string;
  private deleteCommentDialogData!: { reviewId?: number, reviewIndex?: number, commentId?: number };

  private reviews: ChatReviewInterface[] = [];

  private formData: ChatReviewFormDataInterface = {
    chatId: this.feedback.chat.id,
    reviewerId: this.$store.getters.id,
    reviewedId: this.feedback.feedbackTo,
    isDraft: false,
    scores: {},
    tags: [],
    comments: [],
  };

  private async created(): Promise<void> {
    await this.initialize();
    await this.createTagMap();
    this.loading = false;
  }

  @Watch('feedback')
  private async handleFeedbackChange(): Promise<void> {
    this.loading = true;
    await this.initialize();
    this.loading = false;
  }

  private async initialize(): Promise<void> {
    this.reviews = this.feedback.chat.reviews.filter((r) => r.reviewedId === this.feedback.feedbackTo);
    const review = this.feedback.chat.reviews.find((r) => !r.deletedAt && r.reviewedId === this.feedback.feedbackTo);
    if (!review) {
      this.$errorDialog.show('Feedback not found for given user!');
      return;
    }

    this.reviews.forEach((review, reviewIndex) => {
      this.reviews[reviewIndex].comments = review.comments
        .sort((commentA, commentB) => new Date(commentA.timestamp).getTime() - new Date(commentB.timestamp).getTime());
    });

    await this.changeFeedbackStatus();
    this.formData.reviewedId = this.feedback.feedbackTo;
  }

  private async editReview(): Promise<void> {
    if (this.chatComment.trim() !== '') {
      this.$infoDialog.show('You have unsent comment!');
      return;
    }

    try {
      await this.feedbackRepositoryV2.updateChatFeedback(this.feedback.id, this.formData);
      this.addReview();

      this.formData = {
        chatId: this.feedback.chat.id,
        reviewerId: this.$store.getters.id,
        reviewedId: this.feedback.feedbackTo,
        isDraft: false,
        scores: {},
        tags: [],
        comments: [],
      };

      this.showReviewEditSection = false;
      this.$successDialog.show('Review edited successfully');
      await this.initialize();
    } catch (e) {
      this.$errorDialog.show(
        e.response?.data?.message ? e.response.data.message : e.message,
      );
    }
  }

  private async changeFeedbackStatus(closed: boolean = false): Promise<void> {
    const viewer = this.$store.getters.id;

    if (this.feedback.feedbackTo === viewer && this.feedback.status === FeedbackStatus.SENT) {
      await this.feedbackRepositoryV2.changeChatFeedbackStatus(this.feedback.id, FeedbackStatus.SEEN);
      this.feedback.status = FeedbackStatus.SEEN;
      return;
    }

    if (closed && this.$store.getters.id === this.feedback.feedbackFrom) {
      await this.feedbackRepositoryV2.changeChatFeedbackStatus(this.feedback.id, FeedbackStatus.CLOSED);
      this.feedback.status = FeedbackStatus.CLOSED;
      this.$successDialog.show('Feedback closed successfully!');
    }
  }

  private addScore(scoreTypeId: number, scoreGradeId: number): void {
    this.formData.scores[scoreTypeId] = scoreGradeId;
    this.validateForm();
  }

  private removeScore(scoreTypeId: number): void {
    delete this.formData.scores[scoreTypeId];
    this.validateForm();
  }

  private validateForm(): void {
    this.formValid = (Object.values(this.formData.scores).every((score) => !!score))
      && (Object.keys(this.formData.scores).length === this.scoreTypes.length)
      && (!!this.formData.reviewedId)
      && ((!!this.formData.comments.length && !!this.formData.tags.length)
        || (!this.formData.comments.length && !this.formData.tags.length));
  }

  private async createTag(): Promise<void> {
    if (!this.searchTag) {
      return;
    }

    const data = {
      label: this.searchTag,
      color: '#808080FF',
    } as CreateTagInterface;

    const newTag = await this.tagRepositoryV2.createTag(data);
    this.tags.push(newTag);
    this.formData.tags.push((newTag));
    this.formData.tags = this.formData.tags.filter((tag: TagInterface | string) => typeof tag !== 'string');
    this.tagsMap[newTag.id] = newTag;
  }

  private createTagMap(): void {
    this.tags.forEach((tag) => {
      this.tagsMap[tag.id] = tag;
    });
  }

  private showEditBoxForNewReviewComment(index: number, commentText: string): void {
    if (this.commentIndexForEdit === index) {
      this.editCommentText = '';
      this.commentIndexForEdit = -1;

      return;
    }

    this.commentIndexForEdit = index;
    this.editCommentText = commentText;
  }

  private showEditBox(commentId: number, commentText: string): void {
    if (this.commentIdForEdit === commentId) {
      this.editCommentText = '';
      this.commentIdForEdit = -1;
      return;
    }

    this.editCommentText = commentText;
    this.commentIdForEdit = commentId;
  }

  private editNewReviewComment(index: number): void {
    this.formData.comments[index].text = this.editCommentText;
    this.formData.comments[index].rate = this.editCommentRate;
    this.commentIndexForEdit = -1;
  }

  private async editReviewComment(
    reviewId: number,
    reviewIndex: number,
    commentId: number,
    commentIndex: number,
  ):
    Promise<void> {
    try {
      const commentData: CommentCreateInterface = {
        author: this.$store.getters.id,
        text: this.editCommentText,
        rate: this.editCommentRate,
        timestamp: new Date().toISOString(),
      };

      await this.commentRepositoryV2.updateChatReviewComment(reviewId, commentId, commentData);
      this.$successDialog.show('Comment Edited Successfully!');
      this.reviews[reviewIndex].comments[commentIndex].text = this.editCommentText;
      this.reviews[reviewIndex].comments[commentIndex].rate = this.editCommentRate;

      this.commentIdForEdit = -1;
      this.editCommentText = '';
      this.editCommentRate = CommentRate.NOT_RATED;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private showDeleteNewReviewCommentDialog(reviewIndex: number): void {
    this.deleteCommentDialogData = { reviewIndex };
    this.deleteCommentDialogEvent = 'review-comment-deleted';
    this.deleteCommentDialogVisible = true;
  }

  private showDeleteReviewCommentDialog(reviewId: number, reviewIndex: number, commentId: number): void {
    this.deleteCommentDialogData = { reviewId, reviewIndex, commentId };
    this.deleteCommentDialogEvent = 'review-comment-deleted';
    this.deleteCommentDialogVisible = true;
  }

  private async deleteReviewComment(data: { reviewId: number, reviewIndex: number, commentId: number }): Promise<void> {
    if (!data.commentId) {
      this.formData.comments = this.formData.comments.filter((_, index) => index !== data.reviewIndex);
      return;
    }

    try {
      await this.commentRepositoryV2.deleteChatReviewComment(data.reviewId, data.commentId);
      this.$successDialog.show('Comment Deleted Successfully!');
      this.reviews[data.reviewIndex].comments = this.reviews[data.reviewIndex].comments
        .filter((comment) => comment.id !== data.commentId);
      this.deleteCommentDialogVisible = false;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private addReview(): void {
    this.formData.tags = this.formData.tags.map((tag) => ({ ...tag, tagId: tag.id }));
    this.feedback.chat.reviews = this.feedback.chat.reviews.sort((rA, rB) => {
      return new Date(rA.timestamp).getTime() - new Date(rB.timestamp).getTime();
    });

    this.feedback.chat.reviews[this.feedback.chat.reviews.length - 1].deletedAt = new Date().toLocaleString('sv-se');

    this.feedback.chat.reviews.push({
      id: 0,
      reviewedId: this.formData.reviewedId,
      reviewerId: this.formData.reviewerId,
      comments: this.formData.comments.map((c) => ({
        id: 0,
        author: c.author,
        text: c.text,
        rate: c.rate,
        timestamp: c.timestamp,
      })),
      isDraft: this.formData.isDraft,
      scores: Object.keys(this.formData.scores).map((scoreTypeId) => ({
        id: 0,
        scoreTypeId: Number(scoreTypeId),
        scoreGradeId: this.formData.scores[Number(scoreTypeId)],
      })),
      deletedAt: undefined,
      tags: this.formData.tags.map((tag) => ({ tagId: tag.id })),
      timestamp: new Date(),
    });

    if (this.formData.tags.length) {
      this.formData.tags.forEach((tag) => {
        if (this.tagsMap[tag.id]) {
          return;
        }

        this.tagsMap[tag.id] = tag;
      });
    }

    this.commentIdForEdit = -1;
    this.commentIndexForEdit = -1;
  }

  private async createChatComment(): Promise<void> {
    this.chatComment = this.chatComment.trim();
    if (!this.chatComment) {
      return;
    }

    const commentData: CommentCreateInterface = {
      author: this.$store.getters.id,
      text: this.chatComment,
      rate: this.commentRate,
      timestamp: new Date().toISOString(),
      addToDossier: true,
    };

    try {
      this.$successDialog.show('Comment Created Successfully!');
      this.formData.comments.push(commentData);
      this.chatComment = '';
      this.commentRate = CommentRate.NOT_RATED;
      this.validateForm();
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private setCommentRate(rate: CommentRate): void {
    if (rate === this.commentRate) {
      this.commentRate = CommentRate.NOT_RATED;
      return;
    }

    this.commentRate = rate;
  }

  private setEditCommentRate(rate: CommentRate): void {
    if (rate === this.editCommentRate) {
      this.editCommentRate = CommentRate.NOT_RATED;
      return;
    }

    this.editCommentRate = rate;
  }

  private userHasScope(scope: string): boolean {
    return this.$store.getters.scopes.includes(scope);
  }
}
